import PhoneInput from "@/components/PhoneInput";
import { Plus, Trash, UserPlus, Users, X as XIcon } from "@phosphor-icons/react";

/**
 * ClientsSection — dynamic clients list for Company registration/management.
 * Props:
 *   value: Array<{ id, name, email?, phone1, phone2?, showPhone2? }>
 *   onChange: (nextArray) => void
 *   title: string  — section heading
 *   description: string  — optional helper text
 *   testId: string  — root data-testid
 */
export default function ClientsSection({
  value = [],
  onChange,
  title = "عملاء الشركة (اختياري)",
  description = "بإمكانك إضافة العملاء الذين ستقوم الشركة بحجز رحلات لهم لاحقاً",
  testId = "clients-section",
}) {
  const clients = Array.isArray(value) ? value : [];

  const addClient = () => {
    onChange?.([
      ...clients,
      {
        id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        name: "",
        email: "",
        phone1: "",
        phone2: "",
        showPhone2: false,
      },
    ]);
  };

  const removeClient = (id) => {
    onChange?.(clients.filter((c) => c.id !== id));
  };

  const updateClient = (id, field, newVal) => {
    onChange?.(clients.map((c) => (c.id === id ? { ...c, [field]: newVal } : c)));
  };

  return (
    <div
      className="rounded-2xl border border-primary/20 bg-[#0F2148]/60 p-5 md:p-6 space-y-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <Users size={20} weight="duotone" className="text-primary" />
        <h3 className="text-primary font-bold text-lg">{title}</h3>
      </div>
      <p className="text-white/60 text-xs md:text-sm leading-relaxed">
        {description}
      </p>

      {clients.length > 0 && (
        <div className="space-y-3">
          {clients.map((client, index) => (
            <ClientCard
              key={client.id}
              client={client}
              number={index + 1}
              testId={`${testId}-client-${index}`}
              onRemove={() => removeClient(client.id)}
              onUpdate={(field, val) => updateClient(client.id, field, val)}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={addClient}
        data-testid={`${testId}-add-btn`}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold text-sm md:text-base shadow-[0_6px_18px_rgba(201,169,97,0.25)] transition-all active:translate-y-px"
      >
        <UserPlus size={17} weight="bold" />
        {clients.length === 0
          ? "إضافة عميل"
          : (
            <>
              إضافة عميل <span className="num" dir="ltr">{clients.length + 1}</span>
            </>
          )}
      </button>
    </div>
  );
}

function ClientCard({ client, number, testId, onRemove, onUpdate }) {
  return (
    <div
      className="rounded-xl bg-[#050A15] border border-primary/25 hover:border-primary/50 transition-colors p-4 md:p-5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between pb-3 mb-3 border-b border-primary/15">
        <button
          type="button"
          onClick={onRemove}
          className="w-8 h-8 rounded-lg bg-destructive/10 border border-destructive/40 text-destructive hover:bg-destructive hover:text-white inline-flex items-center justify-center transition-colors"
          aria-label="حذف العميل"
          data-testid={`${testId}-remove`}
        >
          <Trash size={13} weight="bold" />
        </button>
        <div className="inline-flex items-center gap-2 text-primary font-bold text-sm">
          العميل <span className="num" dir="ltr">{number}</span>
        </div>
      </div>

      <div className="space-y-3">
        <label className="block">
          <div className="text-xs text-white/60 mb-1.5 text-right">اسم العميل *</div>
          <input
            type="text"
            required
            value={client.name || ""}
            onChange={(e) => onUpdate("name", e.target.value)}
            placeholder="أدخل اسم العميل"
            className="mz-input"
            data-testid={`${testId}-name`}
          />
        </label>

        <label className="block">
          <div className="text-xs text-white/60 mb-1.5 text-right">البريد (اختياري)</div>
          <input
            type="email"
            value={client.email || ""}
            onChange={(e) => onUpdate("email", e.target.value)}
            placeholder="example@email.com"
            className="mz-input text-right"
            dir="ltr"
            data-testid={`${testId}-email`}
          />
        </label>

        <div>
          <div className="text-xs text-white/60 mb-1.5 text-right">جوال العميل *</div>
          <PhoneInput
            value={client.phone1 || ""}
            onChange={(v) => onUpdate("phone1", v)}
            required
            testId={`${testId}-phone1`}
          />
        </div>

        {!client.showPhone2 ? (
          <button
            type="button"
            onClick={() => onUpdate("showPhone2", true)}
            className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-primary/50 hover:border-primary hover:bg-primary/5 text-primary text-xs font-bold transition-all"
            data-testid={`${testId}-add-phone2`}
          >
            <Plus size={12} weight="bold" /> إضافة رقم للعميل
          </button>
        ) : (
          <div>
            <div className="text-xs text-white/60 mb-1.5 text-right">رقم إضافي</div>
            <PhoneInput
              value={client.phone2 || ""}
              onChange={(v) => onUpdate("phone2", v)}
              testId={`${testId}-phone2`}
            />
            <button
              type="button"
              onClick={() => {
                onUpdate("phone2", "");
                onUpdate("showPhone2", false);
              }}
              className="mt-2 inline-flex items-center gap-1 text-destructive text-[11px] hover:underline"
              data-testid={`${testId}-remove-phone2`}
            >
              <XIcon size={11} weight="bold" /> إزالة الرقم الإضافي
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
